/**
 * Shared Tailwind `theme.extend` for Orbit (ported from @imj_media/ui).
 * Includes legacy spacing merge + Figma token scale; keep in sync with tokens.css.
 */
function generateSpacingValues(): Record<string, Record<string, string>> {
  // Definir los valores base una sola vez
  const baseValues = {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    '2xl': '32px',
    '3xl': '48px',
    1: '4px',
    1.5: '6px',
    2: '8px',
    2.5: '10px',
    3: '12px',
    3.5: '14px',
    4: '16px',
    4.5: '18px',
    5: '20px',
    5.5: '22px',
    6: '24px',
    6.5: '26px',
    7: '28px',
    7.5: '30px',
    8: '32px',
    8.5: '34px',
    9: '36px',
    9.5: '38px',
    10: '40px',
    10.5: '42px',
    11: '44px',
    11.5: '46px',
    12: '48px',
    12.5: '50px',
    13: '52px',
    13.5: '54px',
    14: '56px',
    14.5: '58px',
    15: '60px',
    15.5: '62px',
    16: '64px',
    16.5: '66px',
    17: '68px',
    17.5: '70px',
    18: '72px',
    18.5: '74px',
    19: '76px',
    19.5: '78px',
    20: '80px',
    20.5: '82px',
    21: '84px',
    21.5: '86px',
    22: '88px',
    22.5: '90px',
    23: '92px',
    23.5: '94px',
    24: '96px',
    64: '256px',
    80: '320px',
    96: '384px',
    'w-badge': '15px',
    'h-badge': '15px',
  }

  // Propiedades que deben usar estos valores
  const spacingProperties = [
    'spacing',
    'gap',
    'padding',
    'margin',
    'width',
    'height',
    'minWidth',
    'maxWidth',
    'minHeight',
    'maxHeight',
    'inset',
    'left',
    'right',
    'top',
    'bottom',
  ]

  // Generar objeto con todas las propiedades
  const generatedSpacing: Record<string, Record<string, string>> = {}

  spacingProperties.forEach((property) => {
    generatedSpacing[property] = baseValues
  })

  return generatedSpacing
}

// Generar todos los valores de espaciado
const spacingValues = generateSpacingValues()

/**
 * Escala numérica alineada con Figma (`positive/space_*` / `negative/space_*`).
 * Debe aplicarse también a top/left/inset/margin: si no, `ui-top-8` hereda el legacy
 * de generateSpacingValues (8 → 32px) mientras `ui-p-8` sí usa 8px.
 */
const figmaSpacingScale = {
  0: 'var(--ui-Positive-space_0)',
  2: 'var(--ui-Positive-space_2)',
  4: 'var(--ui-Positive-space_4)',
  6: 'var(--ui-Positive-space_6)',
  8: 'var(--ui-Positive-space_8)',
  10: 'var(--ui-Positive-space_10)',
  12: 'var(--ui-Positive-space_12)',
  14: 'var(--ui-Positive-space_14)',
  16: 'var(--ui-Positive-space_16)',
  20: 'var(--ui-Positive-space_20)',
  24: 'var(--ui-Positive-space_24)',
  28: 'var(--ui-Positive-space_28)',
  32: 'var(--ui-Positive-space_32)',
  36: 'var(--ui-Positive-space_36)',
  40: 'var(--ui-Positive-space_40)',
  48: 'var(--ui-Positive-space_48)',
  56: 'var(--ui-Positive-space_56)',
  64: 'var(--ui-Positive-space_64)',
  80: 'var(--ui-Positive-space_80)',
  96: 'var(--ui-Positive-space_96)',
  '-2': 'var(--ui-Negative-space_2)',
  '-4': 'var(--ui-Negative-space_4)',
  '-6': 'var(--ui-Negative-space_6)',
  '-8': 'var(--ui-Negative-space_8)',
  '-10': 'var(--ui-Negative-space_10)',
  '-12': 'var(--ui-Negative-space_12)',
  '-14': 'var(--ui-Negative-space_14)',
  '-16': 'var(--ui-Negative-space_16)',
  '-20': 'var(--ui-Negative-space_20)',
  '-24': 'var(--ui-Negative-space_24)',
  '-28': 'var(--ui-Negative-space_28)',
  '-32': 'var(--ui-Negative-space_32)',
  '-36': 'var(--ui-Negative-space_36)',
  '-40': 'var(--ui-Negative-space_40)',
  '-48': 'var(--ui-Negative-space_48)',
  '-56': 'var(--ui-Negative-space_56)',
  '-64': 'var(--ui-Negative-space_64)',
  '-80': 'var(--ui-Negative-space_80)',
  '-96': 'var(--ui-Negative-space_96)',
}

/**
 * Shadow 3 (Figma shadows/shadow-3, WIB: `--shadow-03-*` y color vía `--color-black-alpha-20`).
 * En runtime usamos tokens de orbit-tokens (`--ui-shadow-03-*`); los fallbacks igualan la exportación Figma.
 */
const uiBoxShadow03 =
  'var(--ui-shadow-03-x, 0) var(--ui-shadow-03-y, 4px) var(--ui-shadow-03-blur, 8px) var(--ui-shadow-03-Spread, -2px) var(--ui-shadow-03-Color, rgba(0, 0, 0, 0.20))';

export function buildOrbitTailwindThemeExtend() {
  return {
      colors: {
        white: 'var(--ui-color-white)',
        black: 'var(--ui-color-black)',
        blue: {
          50: 'var(--ui-color-blue-50)',
          100: 'var(--ui-color-blue-100)',
          200: 'var(--ui-color-blue-200)',
          300: 'var(--ui-color-blue-300)',
          400: 'var(--ui-color-blue-400)',
          500: 'var(--ui-color-blue-500)',
          600: 'var(--ui-color-blue-600)',
          700: 'var(--ui-color-blue-700)',
          800: 'var(--ui-color-blue-800)',
          900: 'var(--ui-color-blue-900)',
        },
        green: {
          50: 'var(--ui-color-green-50)',
          100: 'var(--ui-color-green-100)',
          200: 'var(--ui-color-green-200)',
          300: 'var(--ui-color-green-300)',
          400: 'var(--ui-color-green-400)',
          500: 'var(--ui-color-green-500)',
          600: 'var(--ui-color-green-600)',
          700: 'var(--ui-color-green-700)',
          800: 'var(--ui-color-green-800)',
          900: 'var(--ui-color-green-900)',
        },
        neutral: {
          50: 'var(--ui-color-neutral-50)',
          100: 'var(--ui-color-neutral-100)',
          200: 'var(--ui-color-neutral-200)',
          300: 'var(--ui-color-neutral-300)',
          400: 'var(--ui-color-neutral-400)',
          500: 'var(--ui-color-neutral-500)',
          600: 'var(--ui-color-neutral-600)',
          700: 'var(--ui-color-neutral-700)',
          800: 'var(--ui-color-neutral-800)',
          900: 'var(--ui-color-neutral-900)',
        },
        red: {
          50: 'var(--ui-color-red-50)',
          100: 'var(--ui-color-red-100)',
          200: 'var(--ui-color-red-200)',
          300: 'var(--ui-color-red-300)',
          400: 'var(--ui-color-red-400)',
          500: 'var(--ui-color-red-500)',
          600: 'var(--ui-color-red-600)',
          700: 'var(--ui-color-red-700)',
          800: 'var(--ui-color-red-800)',
          900: 'var(--ui-color-red-900)',
        },
        orange: {
          50: 'var(--ui-color-orange-50)',
          100: 'var(--ui-color-orange-100)',
          200: 'var(--ui-color-orange-200)',
          300: 'var(--ui-color-orange-300)',
          400: 'var(--ui-color-orange-400)',
          500: 'var(--ui-color-orange-500)',
          600: 'var(--ui-color-orange-600)',
          700: 'var(--ui-color-orange-700)',
          800: 'var(--ui-color-orange-800)',
          900: 'var(--ui-color-orange-900)',
        },
        yellow: {
          50: 'var(--ui-color-yellow-50)',
          100: 'var(--ui-color-yellow-100)',
          200: 'var(--ui-color-yellow-200)',
          300: 'var(--ui-color-yellow-300)',
          400: 'var(--ui-color-yellow-400)',
          500: 'var(--ui-color-yellow-500)',
          600: 'var(--ui-color-yellow-600)',
          700: 'var(--ui-color-yellow-700)',
          800: 'var(--ui-color-yellow-800)',
          900: 'var(--ui-color-yellow-900)',
        },
        'blue-dark': {
          50: 'var(--ui-color-blue-dark-50)',
          100: 'var(--ui-color-blue-dark-100)',
          200: 'var(--ui-color-blue-dark-200)',
          300: 'var(--ui-color-blue-dark-300)',
          400: 'var(--ui-color-blue-dark-400)',
          500: 'var(--ui-color-blue-dark-500)',
          600: 'var(--ui-color-blue-dark-600)',
          700: 'var(--ui-color-blue-dark-700)',
          800: 'var(--ui-color-blue-dark-800)',
          900: 'var(--ui-color-blue-dark-900)',
        },
        'blue-light': {
          50: 'var(--ui-color-blue-light-50)',
          100: 'var(--ui-color-blue-light-100)',
          200: 'var(--ui-color-blue-light-200)',
          300: 'var(--ui-color-blue-light-300)',
          400: 'var(--ui-color-blue-light-400)',
          500: 'var(--ui-color-blue-light-500)',
          600: 'var(--ui-color-blue-light-600)',
          700: 'var(--ui-color-blue-light-700)',
          800: 'var(--ui-color-blue-light-800)',
          900: 'var(--ui-color-blue-light-900)',
        },
        gray: {
          50: 'var(--ui-color-gray-50)',
          100: 'var(--ui-color-gray-100)',
          200: 'var(--ui-color-gray-200)',
          300: 'var(--ui-color-gray-300)',
          400: 'var(--ui-color-gray-400)',
          500: 'var(--ui-color-gray-500)',
          600: 'var(--ui-color-gray-600)',
          700: 'var(--ui-color-gray-700)',
          800: 'var(--ui-color-gray-800)',
          900: 'var(--ui-color-gray-900)',
        },

        // === TOKENS DE FIGMA - Colores Adicionales ===
        lime: {
          50: 'var(--ui-color-lime-50)',
          100: 'var(--ui-color-lime-100)',
          200: 'var(--ui-color-lime-200)',
          300: 'var(--ui-color-lime-300)',
          400: 'var(--ui-color-lime-400)',
          500: 'var(--ui-color-lime-500)',
          600: 'var(--ui-color-lime-600)',
          700: 'var(--ui-color-lime-700)',
          800: 'var(--ui-color-lime-800)',
          900: 'var(--ui-color-lime-900)',
        },
        cyan: {
          50: 'var(--ui-color-cyan-50)',
          100: 'var(--ui-color-cyan-100)',
          200: 'var(--ui-color-cyan-200)',
          300: 'var(--ui-color-cyan-300)',
          400: 'var(--ui-color-cyan-400)',
          500: 'var(--ui-color-cyan-500)',
          600: 'var(--ui-color-cyan-600)',
          700: 'var(--ui-color-cyan-700)',
          800: 'var(--ui-color-cyan-800)',
          900: 'var(--ui-color-cyan-900)',
        },
        azure: {
          50: 'var(--ui-color-azure-50)',
          100: 'var(--ui-color-azure-100)',
          200: 'var(--ui-color-azure-200)',
          300: 'var(--ui-color-azure-300)',
          400: 'var(--ui-color-azure-400)',
          500: 'var(--ui-color-azure-500)',
          600: 'var(--ui-color-azure-600)',
          700: 'var(--ui-color-azure-700)',
          800: 'var(--ui-color-azure-800)',
          900: 'var(--ui-color-azure-900)',
        },
        violet: {
          50: 'var(--ui-color-violet-50)',
          100: 'var(--ui-color-violet-100)',
          200: 'var(--ui-color-violet-200)',
          300: 'var(--ui-color-violet-300)',
          400: 'var(--ui-color-violet-400)',
          500: 'var(--ui-color-violet-500)',
          600: 'var(--ui-color-violet-600)',
          700: 'var(--ui-color-violet-700)',
          800: 'var(--ui-color-violet-800)',
          900: 'var(--ui-color-violet-900)',
        },
        magenta: {
          50: 'var(--ui-color-magenta-50)',
          100: 'var(--ui-color-magenta-100)',
          200: 'var(--ui-color-magenta-200)',
          300: 'var(--ui-color-magenta-300)',
          400: 'var(--ui-color-magenta-400)',
          500: 'var(--ui-color-magenta-500)',
          600: 'var(--ui-color-magenta-600)',
          700: 'var(--ui-color-magenta-700)',
          800: 'var(--ui-color-magenta-800)',
          900: 'var(--ui-color-magenta-900)',
        },
        rose: {
          50: 'var(--ui-color-rose-50)',
          100: 'var(--ui-color-rose-100)',
          200: 'var(--ui-color-rose-200)',
          300: 'var(--ui-color-rose-300)',
          400: 'var(--ui-color-rose-400)',
          500: 'var(--ui-color-rose-500)',
          600: 'var(--ui-color-rose-600)',
          700: 'var(--ui-color-rose-700)',
          800: 'var(--ui-color-rose-800)',
          900: 'var(--ui-color-rose-900)',
        },
        'light-gray': {
          50: 'var(--ui-color-light-gray-50)',
          100: 'var(--ui-color-light-gray-100)',
          200: 'var(--ui-color-light-gray-200)',
          300: 'var(--ui-color-light-gray-300)',
          400: 'var(--ui-color-light-gray-400)',
          500: 'var(--ui-color-light-gray-500)',
          600: 'var(--ui-color-light-gray-600)',
          700: 'var(--ui-color-light-gray-700)',
          800: 'var(--ui-color-light-gray-800)',
          900: 'var(--ui-color-light-gray-900)',
        },
        'dark-gray': {
          50: 'var(--ui-color-dark-gray-50)',
          100: 'var(--ui-color-dark-gray-100)',
          200: 'var(--ui-color-dark-gray-200)',
          300: 'var(--ui-color-dark-gray-300)',
          400: 'var(--ui-color-dark-gray-400)',
          500: 'var(--ui-color-dark-gray-500)',
          600: 'var(--ui-color-dark-gray-600)',
          700: 'var(--ui-color-dark-gray-700)',
          800: 'var(--ui-color-dark-gray-800)',
          900: 'var(--ui-color-dark-gray-900)',
        },
      },

      // === TOKENS SEMÁNTICOS DE FIGMA - Colores de Texto ===
      textColor: {
        primary: 'var(--ui-color-text-primary)',
        secondary: 'var(--ui-color-text-secondary)',
        tertiary: 'var(--ui-color-text-tertiary)',
        disabled: 'var(--ui-color-text-disabled)',
        'on-color': 'var(--ui-color-text-text-on-color)',
        'on-canvas-inverse': 'var(--ui-color-text-on-canvas-Inverse)',
        brand: {
          DEFAULT: 'var(--ui-color-text-brand-default)',
          default: 'var(--ui-color-text-brand-default)',
          hover: 'var(--ui-color-text-brand-hover)',
          pressed: 'var(--ui-color-text-brand-pressed)',
          selected: 'var(--ui-color-text-brand-selected)',
        },
        success: {
          DEFAULT: 'var(--ui-color-text-success-default)',
          default: 'var(--ui-color-text-success-default)',
          hover: 'var(--ui-color-text-success-hover)',
          pressed: 'var(--ui-color-text-success-pressed)',
          selected: 'var(--ui-color-text-success-selected)',
        },
        warning: {
          DEFAULT: 'var(--ui-color-text-warning-default)',
          default: 'var(--ui-color-text-warning-default)',
          hover: 'var(--ui-color-text-warning-hover)',
          pressed: 'var(--ui-color-text-warning-pressed)',
          selected: 'var(--ui-color-text-warning-selected)',
        },
        danger: {
          DEFAULT: 'var(--ui-color-text-danger-default)',
          default: 'var(--ui-color-text-danger-default)',
          hover: 'var(--ui-color-text-danger-hover)',
          pressed: 'var(--ui-color-text-danger-pressed)',
          selected: 'var(--ui-color-text-danger-selected)',
        },
        info: {
          DEFAULT: 'var(--ui-color-text-info-default)',
          default: 'var(--ui-color-text-info-default)',
          hover: 'var(--ui-color-text-info-hover)',
          pressed: 'var(--ui-color-text-info-pressed)',
          selected: 'var(--ui-color-text-info-selected)',
        },
        link: {
          DEFAULT: 'var(--ui-color-text-link-default)',
          default: 'var(--ui-color-text-link-default)',
          hover: 'var(--ui-color-text-link-hover)',
          pressed: 'var(--ui-color-text-link-pressed)',
          visited: 'var(--ui-color-text-link-visited)',
        },
        // === TOKENS SEMÁNTICOS DE FIGMA - Colores de Iconos (usando color) ===
        icon: {
          DEFAULT: 'var(--ui-color-icon-default)',
          default: 'var(--ui-color-icon-default)',
          hover: 'var(--ui-color-icon-hover)',
          pressed: 'var(--ui-color-icon-pressed)',
          selected: 'var(--ui-color-icon-selected)',
          disabled: 'var(--ui-color-icon-disabled)',
          'on-color': 'var(--ui-color-icon-on-color)',
          secondary: {
            DEFAULT: 'var(--ui-color-icon-secondary-default)',
            default: 'var(--ui-color-icon-secondary-default)',
            hover: 'var(--ui-color-icon-secondary-hover)',
            pressed: 'var(--ui-color-icon-secondary-pressed)',
            selected: 'var(--ui-color-icon-secondary-selected)',
          },
          tertiary: {
            DEFAULT: 'var(--ui-color-icon-tertiary-default)',
            default: 'var(--ui-color-icon-tertiary-default)',
            hover: 'var(--ui-color-icon-tertiary-hover)',
            pressed: 'var(--ui-color-icon-tertiary-pressed)',
            selected: 'var(--ui-color-icon-tertiary-selected)',
          },
          brand: {
            DEFAULT: 'var(--ui-color-icon-brand-default)',
            default: 'var(--ui-color-icon-brand-default)',
            hover: 'var(--ui-color-icon-brand-hover)',
            pressed: 'var(--ui-color-icon-brand-pressed)',
            selected: 'var(--ui-color-icon-brand-selected)',
          },
          success: {
            DEFAULT: 'var(--ui-color-icon-success-default)',
            default: 'var(--ui-color-icon-success-default)',
            hover: 'var(--ui-color-icon-success-hover)',
            pressed: 'var(--ui-color-icon-success-pressed)',
            selected: 'var(--ui-color-icon-success-selected)',
          },
          warning: {
            DEFAULT: 'var(--ui-color-icon-warning-default)',
            default: 'var(--ui-color-icon-warning-default)',
            hover: 'var(--ui-color-icon-warning-hover)',
            pressed: 'var(--ui-color-icon-warning-pressed)',
            selected: 'var(--ui-color-icon-warning-selected)',
          },
          danger: {
            DEFAULT: 'var(--ui-color-icon-danger-default)',
            default: 'var(--ui-color-icon-danger-default)',
            hover: 'var(--ui-color-icon-danger-hover)',
            pressed: 'var(--ui-color-icon-danger-pressed)',
            selected: 'var(--ui-color-icon-danger-selected)',
          },
          info: {
            DEFAULT: 'var(--ui-color-icon-info-default)',
            default: 'var(--ui-color-icon-info-default)',
            hover: 'var(--ui-color-icon-info-hover)',
            pressed: 'var(--ui-color-icon-info-pressed)',
            selected: 'var(--ui-color-icon-info-selected)',
          },
          'all-colors': {
            red: 'var(--ui-color-icon-all-colors-red)',
            orange: 'var(--ui-color-icon-all-colors-orange)',
            yellow: 'var(--ui-color-icon-all-colors-yellow)',
            lime: 'var(--ui-color-icon-all-colors-lime)',
            green: 'var(--ui-color-icon-all-colors-green)',
            cyan: 'var(--ui-color-icon-all-colors-cyan)',
            azure: 'var(--ui-color-icon-all-colors-azure)',
            blue: 'var(--ui-color-icon-all-colors-blue)',
            violet: 'var(--ui-color-icon-all-colors-violet)',
            magenta: 'var(--ui-color-icon-all-colors-magenta)',
            rose: 'var(--ui-color-icon-all-colors-rose)',
          },
        },
        'all-colors': {
          red: 'var(--ui-color-text-all-colors-red)',
          orange: 'var(--ui-color-text-all-colors-orange)',
          yellow: 'var(--ui-color-text-all-colors-yellow)',
          lime: 'var(--ui-color-text-all-colors-lime)',
          green: 'var(--ui-color-text-all-colors-green)',
          cyan: 'var(--ui-color-text-all-colors-cyan)',
          azure: 'var(--ui-color-text-all-colors-azure)',
          blue: 'var(--ui-color-text-all-colors-blue)',
          violet: 'var(--ui-color-text-all-colors-violet)',
          magenta: 'var(--ui-color-text-all-colors-magenta)',
          rose: 'var(--ui-color-text-all-colors-rose)',
        },
        // === CUSTOM ALERT COLORS ===
        'alert-info-text': 'var(--ui-color-alert-info-text)',
        'alert-success-text': 'var(--ui-color-alert-success-text)',
        'alert-danger-text': 'var(--ui-color-alert-danger-text)',
        'alert-warning-text': 'var(--ui-color-alert-warning-text)',
        'alert-info-icon': 'var(--ui-color-alert-info-icon)',
        'alert-success-icon': 'var(--ui-color-alert-success-icon)',
        'alert-danger-icon': 'var(--ui-color-alert-danger-icon)',
        'alert-warning-icon': 'var(--ui-color-alert-warning-icon)',
      },

      // === TOKENS SEMÁNTICOS DE FIGMA - Colores de Fondo ===
      backgroundColor: {
        primary: 'var(--ui-color-text-primary)',
        secondary: 'var(--ui-color-text-secondary)',
        tertiary: 'var(--ui-color-text-tertiary)',
        disabled: 'var(--ui-color-text-disabled)',
        canvas: {
          DEFAULT: 'var(--ui-color-bg-canvas-default)',
          default: 'var(--ui-color-bg-canvas-default)',
          inverse: 'var(--ui-color-bg-canvas-inverse)',
        },
        overlay: {
          DEFAULT: 'var(--ui-color-bg-overlay-default)',
          default: 'var(--ui-color-bg-overlay-default)',
          darker: 'var(--ui-color-bg-overlay-darker)',
        },
        surface: {
          DEFAULT: 'var(--ui-color-bg-surface-default)',
          default: 'var(--ui-color-bg-surface-default)',
          hover: 'var(--ui-color-bg-surface-hover)',
          pressed: 'var(--ui-color-bg-surface-pressed)',
          selected: 'var(--ui-color-bg-surface-selected)',
          disabled: 'var(--ui-color-bg-surface-dissabled)',
          secondary: {
            DEFAULT: 'var(--ui-color-bg-surface-secondary-default)',
            default: 'var(--ui-color-bg-surface-secondary-default)',
            hover: 'var(--ui-color-bg-surface-secondary-hover)',
            pressed: 'var(--ui-color-bg-surface-secondary-pressed)',
            selected: 'var(--ui-color-bg-surface-secondary-selected)',
          },
          tertiary: {
            DEFAULT: 'var(--ui-color-bg-surface-tertiary-default)',
            default: 'var(--ui-color-bg-surface-tertiary-default)',
            hover: 'var(--ui-color-bg-surface-tertiary-hover)',
            pressed: 'var(--ui-color-bg-surface-tertiary-pressed)',
            selected: 'var(--ui-color-bg-surface-tertiary-selected)',
          },
          brand: {
            DEFAULT: 'var(--ui-color-bg-surface-brand-default)',
            default: 'var(--ui-color-bg-surface-brand-default)',
            hover: 'var(--ui-color-bg-surface-brand-hover)',
            pressed: 'var(--ui-color-bg-surface-brand-pressed)',
            selected: 'var(--ui-color-bg-surface-brand-selected)',
            disabled: 'var(--ui-color-bg-surface-brand-disabled)',
          },
          success: {
            DEFAULT: 'var(--ui-color-bg-surface-success-default)',
            default: 'var(--ui-color-bg-surface-success-default)',
            hover: 'var(--ui-color-bg-surface-success-hover)',
            pressed: 'var(--ui-color-bg-surface-success-pressed)',
            selected: 'var(--ui-color-bg-surface-success-selected)',
            disabled: 'var(--ui-color-bg-surface-success-disabled)',
          },
          warning: {
            DEFAULT: 'var(--ui-color-bg-surface-warning-default)',
            default: 'var(--ui-color-bg-surface-warning-default)',
            hover: 'var(--ui-color-bg-surface-warning-hover)',
            pressed: 'var(--ui-color-bg-surface-warning-pressed)',
            selected: 'var(--ui-color-bg-surface-warning-selected)',
            disabled: 'var(--ui-color-bg-surface-warning-disabled)',
          },
          danger: {
            DEFAULT: 'var(--ui-color-bg-surface-danger-default)',
            default: 'var(--ui-color-bg-surface-danger-default)',
            hover: 'var(--ui-color-bg-surface-danger-hover)',
            pressed: 'var(--ui-color-bg-surface-danger-pressed)',
            selected: 'var(--ui-color-bg-surface-danger-selected)',
            disabled: 'var(--ui-color-bg-surface-danger-disabled)',
          },
          info: {
            DEFAULT: 'var(--ui-color-bg-surface-info-default)',
            default: 'var(--ui-color-bg-surface-info-default)',
            hover: 'var(--ui-color-bg-surface-info-hover)',
            pressed: 'var(--ui-color-bg-surface-info-pressed)',
            selected: 'var(--ui-color-bg-surface-info-selected)',
            disabled: 'var(--ui-color-bg-surface-info-disabled)',
          },
          'all-colors': {
            red: 'var(--ui-color-bg-surface-all-colors-red)',
            orange: 'var(--ui-color-bg-surface-all-colors-orange)',
            yellow: 'var(--ui-color-bg-surface-all-colors-yellow)',
            lime: 'var(--ui-color-bg-surface-all-colors-lime)',
            green: 'var(--ui-color-bg-surface-all-colors-green)',
            cyan: 'var(--ui-color-bg-surface-all-colors-cyan)',
            azure: 'var(--ui-color-bg-surface-all-colors-azure)',
            blue: 'var(--ui-color-bg-surface-all-colors-blue)',
            violet: 'var(--ui-color-bg-surface-all-colors-violet)',
            magenta: 'var(--ui-color-bg-surface-all-colors-magenta)',
            rose: 'var(--ui-color-bg-surface-all-colors-rose)',
          },
        },
        fill: {
          DEFAULT: 'var(--ui-color-bg-fill-default)',
          default: 'var(--ui-color-bg-fill-default)',
          hover: 'var(--ui-color-bg-fill-hover)',
          pressed: 'var(--ui-color-bg-fill-pressed)',
          selected: 'var(--ui-color-bg-fill-selected)',
          disabled: 'var(--ui-color-bg-fill-dissabled)',
          secondary: {
            DEFAULT: 'var(--ui-color-bg-fill-secondary-default)',
            default: 'var(--ui-color-bg-fill-secondary-default)',
            hover: 'var(--ui-color-bg-fill-secondary-hover)',
            pressed: 'var(--ui-color-bg-fill-secondary-pressed)',
            selected: 'var(--ui-color-bg-fill-secondary-selected)',
          },
          tertiary: {
            DEFAULT: 'var(--ui-color-bg-fill-tertiary-default)',
            default: 'var(--ui-color-bg-fill-tertiary-default)',
            hover: 'var(--ui-color-bg-fill-tertiary-hover)',
            pressed: 'var(--ui-color-bg-fill-tertiary-pressed)',
            selected: 'var(--ui-color-bg-fill-tertiary-selected)',
          },
          brand: {
            DEFAULT: 'var(--ui-color-bg-fill-brand-default)',
            default: 'var(--ui-color-bg-fill-brand-default)',
            hover: 'var(--ui-color-bg-fill-brand-hover)',
            pressed: 'var(--ui-color-bg-fill-brand-pressed)',
            selected: 'var(--ui-color-bg-fill-brand-selected)',
            disabled: 'var(--ui-color-bg-fill-brand-disabled)',
          },
          success: {
            DEFAULT: 'var(--ui-color-bg-fill-success-default)',
            default: 'var(--ui-color-bg-fill-success-default)',
            hover: 'var(--ui-color-bg-fill-success-hover)',
            pressed: 'var(--ui-color-bg-fill-success-pressed)',
            selected: 'var(--ui-color-bg-fill-success-selected)',
            disabled: 'var(--ui-color-bg-fill-success-disabled)',
          },
          warning: {
            DEFAULT: 'var(--ui-color-bg-fill-warning-default)',
            default: 'var(--ui-color-bg-fill-warning-default)',
            hover: 'var(--ui-color-bg-fill-warning-hover)',
            pressed: 'var(--ui-color-bg-fill-warning-pressed)',
            selected: 'var(--ui-color-bg-fill-warning-selected)',
            disabled: 'var(--ui-color-bg-fill-warning-disabled)',
          },
          danger: {
            DEFAULT: 'var(--ui-color-bg-fill-danger-default)',
            default: 'var(--ui-color-bg-fill-danger-default)',
            hover: 'var(--ui-color-bg-fill-danger-hover)',
            pressed: 'var(--ui-color-bg-fill-danger-pressed)',
            selected: 'var(--ui-color-bg-fill-danger-selected)',
            disabled: 'var(--ui-color-bg-fill-danger-pressed-2)',
          },
          info: {
            DEFAULT: 'var(--ui-color-bg-fill-info-default)',
            default: 'var(--ui-color-bg-fill-info-default)',
            hover: 'var(--ui-color-bg-fill-info-hover)',
            pressed: 'var(--ui-color-bg-fill-info-pressed)',
            selected: 'var(--ui-color-bg-fill-info-selected)',
            disabled: 'var(--ui-color-bg-fill-info-pressed-2)',
          },
          'all-colors': {
            red: 'var(--ui-color-bg-fill-all-colors-red)',
            orange: 'var(--ui-color-bg-fill-all-colors-orange)',
            yellow: 'var(--ui-color-bg-fill-all-colors-yellow)',
            lime: 'var(--ui-color-bg-fill-all-colors-lime)',
            green: 'var(--ui-color-bg-fill-all-colors-green)',
            cyan: 'var(--ui-color-bg-fill-all-colors-cyan)',
            azure: 'var(--ui-color-bg-fill-all-colors-azure)',
            blue: 'var(--ui-color-bg-fill-all-colors-blue)',
            violet: 'var(--ui-color-bg-fill-all-colors-violet)',
            magenta: 'var(--ui-color-bg-fill-all-colors-magenta)',
            rose: 'var(--ui-color-bg-fill-all-colors-rose)',
          },
        },
        // === CUSTOM ALERT COLORS ===
        'alert-info-bg': 'var(--ui-color-alert-info-bg)',
        'alert-success-bg': 'var(--ui-color-alert-success-bg)',
        'alert-danger-bg': 'var(--ui-color-alert-danger-bg)',
        'alert-warning-bg': 'var(--ui-color-alert-warning-bg)',
      },

      // === TOKENS SEMÁNTICOS DE FIGMA - Colores de Bordes ===
      borderColor: {
        DEFAULT: 'var(--ui-color-border-default)',
        default: 'var(--ui-color-border-default)',
        hover: 'var(--ui-color-border-hover)',
        pressed: 'var(--ui-color-border-pressed)',
        focus: 'var(--ui-color-border-focus)',
        disabled: 'var(--ui-color-border-disabled)',
        subtle: 'var(--ui-color-border-subtle)',
        'on-color': 'var(--ui-color-border-on-color)',
        strong: 'var(--ui-color-border-strong)',
        success: 'var(--ui-color-border-success)',
        warning: 'var(--ui-color-border-warning)',
        danger: 'var(--ui-color-border-danger)',
        info: 'var(--ui-color-border-info)',
        brand: {
          DEFAULT: 'var(--ui-color-border-brand)',
          default: 'var(--ui-color-border-brand)',
          hover: 'var(--ui-color-bg-fill-brand-hover)',
          pressed: 'var(--ui-color-bg-fill-brand-pressed)',
          selected: 'var(--ui-color-bg-fill-brand-selected)',
          disabled: 'var(--ui-color-bg-fill-brand-disabled)',
        },
        fill: {
          DEFAULT: 'var(--ui-color-bg-fill-default)',
          default: 'var(--ui-color-bg-fill-default)',
          hover: 'var(--ui-color-bg-fill-hover)',
          pressed: 'var(--ui-color-bg-fill-pressed)',
          selected: 'var(--ui-color-bg-fill-selected)',
          disabled: 'var(--ui-color-bg-fill-dissabled)',
          brand: {
            DEFAULT: 'var(--ui-color-bg-fill-brand-default)',
            default: 'var(--ui-color-bg-fill-brand-default)',
            hover: 'var(--ui-color-bg-fill-brand-hover)',
            pressed: 'var(--ui-color-bg-fill-brand-pressed)',
            selected: 'var(--ui-color-bg-fill-brand-selected)',
            disabled: 'var(--ui-color-bg-fill-brand-disabled)',
          },
          tertiary: {
            DEFAULT: 'var(--ui-color-bg-fill-tertiary-default)',
            default: 'var(--ui-color-bg-fill-tertiary-default)',
            hover: 'var(--ui-color-bg-fill-tertiary-hover)',
            pressed: 'var(--ui-color-bg-fill-tertiary-pressed)',
            selected: 'var(--ui-color-bg-fill-tertiary-selected)',
            disabled: 'var(--ui-color-bg-fill-tertiary-disabled)',
          },
        },
        'all-colors-red': 'var(--ui-color-border-all-colors-red)',
        'all-colors-orange': 'var(--ui-color-border-all-colors-orange)',
        'all-colors-yellow': 'var(--ui-color-border-all-colors-yellow)',
        'all-colors-lime': 'var(--ui-color-border-all-colors-lime)',
        'all-colors-green': 'var(--ui-color-border-all-colors-green)',
        'all-colors-cyan': 'var(--ui-color-border-all-colors-cyan)',
        'all-colors-azure': 'var(--ui-color-border-all-colors-azure)',
        'all-colors-blue': 'var(--ui-color-border-all-colors-blue)',
        'all-colors-violet': 'var(--ui-color-border-all-colors-violet)',
        'all-colors-magenta': 'var(--ui-color-border-all-colors-magenta)',
        'all-colors-rose': 'var(--ui-color-border-all-colors-rose)',
        // === CUSTOM ALERT COLORS ===
        'alert-info-border': 'var(--ui-color-alert-info-border)',
        'alert-success-border': 'var(--ui-color-alert-success-border)',
        'alert-danger-border': 'var(--ui-color-alert-danger-border)',
        'alert-warning-border': 'var(--ui-color-alert-warning-border)',
      },

      // === TOKENS SEMÁNTICOS DE FIGMA - Colores de Iconos (usando fill) ===
      fill: {
        DEFAULT: 'var(--ui-color-icon-default)',
        default: 'var(--ui-color-icon-default)',
        hover: 'var(--ui-color-icon-hover)',
        pressed: 'var(--ui-color-icon-pressed)',
        selected: 'var(--ui-color-icon-selected)',
        disabled: 'var(--ui-color-icon-disabled)',
        'on-color': 'var(--ui-color-icon-on-color)',
        secondary: {
          DEFAULT: 'var(--ui-color-dark-gray-200)',
          default: 'var(--ui-color-dark-gray-200)',
        },
        brand: {
          DEFAULT: 'var(--ui-color-icon-brand-default)',
          default: 'var(--ui-color-icon-brand-default)',
          hover: 'var(--ui-color-icon-brand-hover)',
          pressed: 'var(--ui-color-icon-brand-pressed)',
          selected: 'var(--ui-color-icon-brand-selected)',
        },
        success: {
          DEFAULT: 'var(--ui-color-icon-success-default)',
          default: 'var(--ui-color-icon-success-default)',
          hover: 'var(--ui-color-icon-success-hover)',
          pressed: 'var(--ui-color-icon-success-pressed)',
          selected: 'var(--ui-color-icon-success-selected)',
        },
        warning: {
          DEFAULT: 'var(--ui-color-icon-warning-default)',
          default: 'var(--ui-color-icon-warning-default)',
          hover: 'var(--ui-color-icon-warning-hover)',
          pressed: 'var(--ui-color-icon-warning-pressed)',
          selected: 'var(--ui-color-icon-warning-selected)',
        },
        danger: {
          DEFAULT: 'var(--ui-color-icon-danger-default)',
          default: 'var(--ui-color-icon-danger-default)',
          hover: 'var(--ui-color-icon-danger-hover)',
          pressed: 'var(--ui-color-icon-danger-pressed)',
          selected: 'var(--ui-color-icon-danger-selected)',
        },
        info: {
          DEFAULT: 'var(--ui-color-icon-info-default)',
          default: 'var(--ui-color-icon-info-default)',
          hover: 'var(--ui-color-icon-info-hover)',
          pressed: 'var(--ui-color-icon-info-pressed)',
          selected: 'var(--ui-color-icon-info-selected)',
        },
        allColors: {
          red: 'var(--ui-color-icon-all-colors-red)',
          orange: 'var(--ui-color-icon-all-colors-orange)',
          yellow: 'var(--ui-color-icon-all-colors-yellow)',
          lime: 'var(--ui-color-icon-all-colors-lime)',
          green: 'var(--ui-color-icon-all-colors-green)',
          cyan: 'var(--ui-color-icon-all-colors-cyan)',
          azure: 'var(--ui-color-icon-all-colors-azure)',
          blue: 'var(--ui-color-icon-all-colors-blue)',
          violet: 'var(--ui-color-icon-all-colors-violet)',
          magenta: 'var(--ui-color-icon-all-colors-magenta)',
          rose: 'var(--ui-color-icon-all-colors-rose)',
        },
      },

      fontFamily: {
        // Single source: orbit-tokens --ui-font-family-ui (optional --app-font-family on consumer :root)
        sans: 'var(--ui-font-family-ui)',
        // === TOKENS DE FIGMA ===
        ui: 'var(--ui-font-family-ui)',
        'ui-text': 'var(--ui-font-family-ui)',
        'ui-rounded': 'var(--ui-font-family-ui)',
        mono: 'var(--ui-font-family-mono)',
      },

      // === TOKENS DE FIGMA - Font Weight ===
      fontWeight: {
        // Valores numéricos estándar
        100: '100',
        200: '200',
        300: '300',
        400: '400',
        500: '500',
        600: '600',
        700: '700',
        800: '800',
        900: '900',

        // Base weights de Figma
        regular: 'var(--ui-font-weight-regular)', // 400
        medium: 'var(--ui-font-weight-medium)', // 500
        semibold: 'var(--ui-font-weight-semibold)', // 600
        bold: 'var(--ui-font-weight-bold)', // 700

        // Typography weights
        // Headings
        'heading-h1': 'var(--ui-type-heading-h1-weight)',
        'heading-h2': 'var(--ui-type-heading-h2-weight)',
        'heading-h3': 'var(--ui-type-heading-h3-weight)',
        'heading-h4': 'var(--ui-type-heading-h4-weight)',
        'heading-h5': 'var(--ui-type-heading-h5-weight)',
        'heading-h6': 'var(--ui-type-heading-h6-weight)',

        // Body XS
        'body-xs-regular': 'var(--ui-type-body-xs-Regular-weight)',
        'body-xs-medium': 'var(--ui-type-body-xs-Medium-weight)',
        'body-xs-semibold': 'var(--ui-type-body-xs-Semibold-weight)',
        'body-xs-bold': 'var(--ui-type-body-xs-Bold-weight)',

        // Body SM
        'body-sm-regular': 'var(--ui-type-body-sm-Regular-weight)',
        'body-sm-medium': 'var(--ui-type-body-sm-Medium-weight)',
        'body-sm-semibold': 'var(--ui-type-body-sm-Semibold-weight)',
        'body-sm-bold': 'var(--ui-type-body-sm-Bold-weight)',

        // Body MD
        'body-md-regular': 'var(--ui-type-body-md-Regular-weight)',
        'body-md-medium': 'var(--ui-type-body-md-Medium-weight)',
        'body-md-semibold': 'var(--ui-type-body-md-Semibold-weight)',
        'body-md-bold': 'var(--ui-type-body-md-Bold-weight)',

        // Body LG
        'body-lg-regular': 'var(--ui-type-body-lg-Regular-weight)',
        'body-lg-medium': 'var(--ui-type-body-lg-Medium-weight)',
        'body-lg-semibold': 'var(--ui-type-body-lg-Semibold-weight)',
        'body-lg-bold': 'var(--ui-type-body-lg-Bold-weight)',

        // Label MD
        'label-md-regular': 'var(--ui-type-label-md-Regular-weight)',
        'label-md-medium': 'var(--ui-type-label-md-Medium-weight)',
        'label-md-semibold': 'var(--ui-type-label-md-Semibold-weight)',
        'label-md-bold': 'var(--ui-type-label-md-Bold-weight)',

        // Caption
        'caption-xs': 'var(--ui-type-caption-xs-weight)',
        'caption-md': 'var(--ui-type-caption-md-weight)',

        // Code
        'code-md': 'var(--ui-type-code-md-weight)',
      },

      // === TOKENS DE FIGMA - Letter Spacing ===
      letterSpacing: {
        tight: 'var(--ui-font-track-tight)',
        normal: 'var(--ui-font-track-normal)',
        wide: 'var(--ui-font-track-wide)',

        // Typography letter spacing
        'heading-h1': 'var(--ui-type-heading-h1-tracking)',
        'heading-h2': 'var(--ui-type-heading-h2-tracking)',
        'heading-h3': 'var(--ui-type-heading-h3-tracking)',
        'heading-h4': 'var(--ui-type-heading-h4-tracking)',
        'heading-h5': 'var(--ui-type-heading-h5-tracking)',
        'heading-h6': 'var(--ui-type-heading-h6-tracking)',

        // Body
        'body-xs': 'var(--ui-type-body-xs-Regular-tracking)',
        'body-sm': 'var(--ui-type-body-sm-Regular-tracking)',
        'body-md': 'var(--ui-type-body-md-Regular-tracking)',
        'body-lg': 'var(--ui-type-body-lg-Regular-tracking)',

        // Label
        'label-md': 'var(--ui-type-label-md-Regular-tracking)',

        // Caption
        'caption-xs': 'var(--ui-type-caption-xs-tracking)',
        'caption-md': 'var(--ui-type-caption-md-tracking)',

        // Code
        'code-md': 'var(--ui-type-code-md-tracking)',
      },
      // === TOKENS DE FIGMA - Line Height ===
      lineHeight: {
        // Headings
        'heading-h1': 'var(--ui-type-heading-h1-ih)',
        'heading-h2': 'var(--ui-type-heading-h2-ih)',
        'heading-h3': 'var(--ui-type-heading-h3-ih)',
        'heading-h4': 'var(--ui-type-heading-h4-ih)',
        'heading-h5': 'var(--ui-type-heading-h5-ih)',
        'heading-h6': 'var(--ui-type-heading-h6-ih)',

        // Body XS
        'body-xs-regular': 'var(--ui-type-body-xs-Regular-ih)',
        'body-xs-medium': 'var(--ui-type-body-xs-Medium-ih)',
        'body-xs-semibold': 'var(--ui-type-body-xs-Semibold-ih)',
        'body-xs-bold': 'var(--ui-type-body-xs-Bold-ih)',

        // Body SM
        'body-sm-regular': 'var(--ui-type-body-sm-Regular-ih)',
        'body-sm-medium': 'var(--ui-type-body-sm-Medium-ih)',
        'body-sm-semibold': 'var(--ui-type-body-sm-Semibold-ih)',
        'body-sm-bold': 'var(--ui-type-body-sm-Bold-ih)',

        // Body MD
        'body-md-regular': 'var(--ui-type-body-md-Regular-ih)',
        'body-md-medium': 'var(--ui-type-body-md-Medium-ih)',
        'body-md-semibold': 'var(--ui-type-body-md-Semibold-ih)',
        'body-md-bold': 'var(--ui-type-body-md-Bold-ih)',

        // Body LG
        'body-lg-regular': 'var(--ui-type-body-lg-Regular-ih)',
        'body-lg-medium': 'var(--ui-type-body-lg-Medium-ih)',
        'body-lg-semibold': 'var(--ui-type-body-lg-Semibold-ih)',
        'body-lg-bold': 'var(--ui-type-body-lg-Bold-ih)',

        // Label MD
        'label-md-regular': 'var(--ui-type-label-md-Regular-ih)',
        'label-md-medium': 'var(--ui-type-label-md-Medium-ih)',
        'label-md-semibold': 'var(--ui-type-label-md-Semibold-ih)',
        'label-md-bold': 'var(--ui-type-label-md-Bold-ih)',

        // Caption
        'caption-xs': 'var(--ui-type-caption-xs-ih)',
        'caption-md': 'var(--ui-type-caption-md-ih)',

        // Code
        'code-md': 'var(--ui-type-code-md-ih)',
      },

      borderWidth: {
        1: '1px',
        2: '2px',
        3: '3px',
        4: '4px',
        5: '5px',
        6: '6px',
        7: '7px',
        8: '8px',
      },
      fontSize: {
        // === TOKENS DE FIGMA - Font Size Base ===
        '2xs': 'var(--ui-font-size-2xs)', // 10px
        xs: 'var(--ui-font-size-xs)', // 12px
        sm: 'var(--ui-font-size-sm)', // 14px
        md: 'var(--ui-font-size-md)', // 16px
        lg: 'var(--ui-font-size-lg)', // 18px
        xl: 'var(--ui-font-size-xl)', // 20px
        '2xl': 'var(--ui-font-size-2xl)', // 24px
        '3xl': 'var(--ui-font-size-3xl)', // 30px
        '4xl': 'var(--ui-font-size-4xl)', // 36px
        '5xl': 'var(--ui-font-size-5xl)', // 48px

        // === TOKENS DE FIGMA - Typography Sizes ===
        // Headings
        'heading-h1': 'var(--ui-type-heading-h1-size)',
        'heading-h2': 'var(--ui-type-heading-h2-size)',
        'heading-h3': 'var(--ui-type-heading-h3-size)',
        'heading-h4': 'var(--ui-type-heading-h4-size)',
        'heading-h5': 'var(--ui-type-heading-h5-size)',
        'heading-h6': 'var(--ui-type-heading-h6-size)',

        // Body XS
        'body-xs-regular': 'var(--ui-type-body-xs-Regular-size)',
        'body-xs-medium': 'var(--ui-type-body-xs-Medium-size)',
        'body-xs-semibold': 'var(--ui-type-body-xs-Semibold-size)',
        'body-xs-bold': 'var(--ui-type-body-xs-Bold-size)',

        // Body SM
        'body-sm-regular': 'var(--ui-type-body-sm-Regular-size)',
        'body-sm-medium': 'var(--ui-type-body-sm-Medium-size)',
        'body-sm-semibold': 'var(--ui-type-body-sm-Semibold-size)',
        'body-sm-bold': 'var(--ui-type-body-sm-Bold-size)',

        // Body MD
        'body-md-regular': 'var(--ui-type-body-md-Regular-size)',
        'body-md-medium': 'var(--ui-type-body-md-Medium-size)',
        'body-md-semibold': 'var(--ui-type-body-md-Semibold-size)',
        'body-md-bold': 'var(--ui-type-body-md-Bold-size)',

        // Body LG
        'body-lg-regular': 'var(--ui-type-body-lg-Regular-size)',
        'body-lg-medium': 'var(--ui-type-body-lg-Medium-size)',
        'body-lg-semibold': 'var(--ui-type-body-lg-Semibold-size)',
        'body-lg-bold': 'var(--ui-type-body-lg-Bold-size)',

        // Label MD
        'label-md-regular': 'var(--ui-type-label-md-Regular-size)',
        'label-md-medium': 'var(--ui-type-label-md-Medium-size)',
        'label-md-semibold': 'var(--ui-type-label-md-Semibold-size)',
        'label-md-bold': 'var(--ui-type-label-md-Bold-size)',

        // Caption
        'caption-xs': 'var(--ui-type-caption-xs-size)',
        'caption-md': 'var(--ui-type-caption-md-size)',

        // Code
        'code-md': 'var(--ui-type-code-md-size)',

        // Valores legacy adicionales
        xxs: '10px', // Alias de 2xs
        base: '16px', // Alias de md
        '6xl': '60px',
        '7xl': '72px',
        '8xl': '96px',
        '9xl': '128px',
      },
      borderRadius: {
        // Valores legacy
        sm: '4px',
        md: '6px',
        lg: '8px',
        xl: '12px',
        '2xl': '16px',

        // === TOKENS DE FIGMA - Border Radius Semánticos ===
        'control-xs': 'var(--ui-radius-control-xs)',
        'control-sm': 'var(--ui-radius-control-sm)',
        'control-md': 'var(--ui-radius-control-md)',
        'control-lg': 'var(--ui-radius-control-lg)',
        'control-pill': 'var(--ui-radius-control-pill)',
        'surface-card': 'var(--ui-radius-surface-card)',
        'surface-modal': 'var(--ui-radius-surface-modal)',
        'surface-popover': 'var(--ui-radius-surface-popover)',
        'surface-tooltip': 'var(--ui-radius-surface-tooltip)',
        'surface-sheet': 'var(--ui-radius-surface-sheet)',
        'surface-forms': 'var(--ui-radius-surface-forms)',
        'surface-cuadrado': 'var(--ui-radius-surface-cuadrado)',
        'image-na': 'var(--ui-radius-image-na)',
        'image-sm': 'var(--ui-radius-image-sm)',
        'image-md': 'var(--ui-radius-image-md)',
        'image-pill': 'var(--ui-radius-image-pill)',
      },
      boxShadow: {
        // Valores legacy
        button: '0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px -1px rgba(0, 0, 0, 0.10)',
        xs: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
        sm: '0px 1px 2px -1px rgba(0, 0, 0, 0.10), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)',
        md: '0px 2px 4px -2px rgba(0, 0, 0, 0.10), 0px 4px 5px -1px rgba(0, 0, 0, 0.10)',
        lg: '0px 4px 6px -4px rgba(0, 0, 0, 0.10), 0px 10px 15px -3px rgba(0, 0, 0, 0.10)',
        xl: '0px 8px 10px -6px rgba(0, 0, 0, 0.10), 0px 20px 25px -5px rgba(0, 0, 0, 0.10)',

        // === TOKENS DE FIGMA - Box Shadow ===
        '01': 'var(--ui-shadow-01-x) var(--ui-shadow-01-y) var(--ui-shadow-01-blur) var(--ui-shadow-01-Spread) var(--ui-shadow-01-Color)',
        /** Catálogo (y similares): en hover se mantiene elevación base (shadow-1) y se suma shadow-3 (Figma). */
        '01-03': `var(--ui-shadow-01-x) var(--ui-shadow-01-y) var(--ui-shadow-01-blur) var(--ui-shadow-01-Spread) var(--ui-shadow-01-Color), ${uiBoxShadow03}`,
        '02': 'var(--ui-shadow-02-x) var(--ui-shadow-02-y) var(--ui-shadow-02-blur) var(--ui-shadow-02-Spread) var(--ui-shadow-02-Color)',
        '03': uiBoxShadow03,
        '04': 'var(--ui-shadow-04-x) var(--ui-shadow-04-y) var(--ui-shadow-04-blur) var(--ui-shadow-04-Spread) var(--ui-shadow-04-Color)',
        '05': 'var(--ui-shadow-05-x) var(--ui-shadow-05-y) var(--ui-shadow-05-blur) var(--ui-shadow-05-Spread) var(--ui-shadow-05-Color)',
      },

      // === TOKENS DE FIGMA - Espaciado Positivo ===
      spacing: {
        ...spacingValues.spacing,
        ...figmaSpacingScale,
      },

      // === TOKENS DE FIGMA - Tamaños (Width y Height) ===
      width: {
        // Tamaños de controles
        'control-xxs': 'var(--ui-size-control-xxs)',
        'control-xs': 'var(--ui-size-control-xs)',
        'control-sm': 'var(--ui-size-control-sm)',
        'control-md': 'var(--ui-size-control-md)',
        'control-lg': 'var(--ui-size-control-lg)',

        // Tamaños de iconos
        'icon-xs': 'var(--ui-size-Icon-xs)',
        'icon-sm': 'var(--ui-size-Icon-sm)',
        'icon-md': 'var(--ui-size-Icon-md)',
        'icon-lg': 'var(--ui-size-Icon-lg)',
        'icon-xl': 'var(--ui-size-Icon-xl)',
      },

      height: {
        // Tamaños de controles
        'control-xxs': 'var(--ui-size-control-xxs)',
        'control-xs': 'var(--ui-size-control-xs)',
        'control-sm': 'var(--ui-size-control-sm)',
        'control-md': 'var(--ui-size-control-md)',
        'control-lg': 'var(--ui-size-control-lg)',

        // Tamaños de iconos
        'icon-xs': 'var(--ui-size-Icon-xs)',
        'icon-sm': 'var(--ui-size-Icon-sm)',
        'icon-md': 'var(--ui-size-Icon-md)',
        'icon-lg': 'var(--ui-size-Icon-lg)',
        'icon-xl': 'var(--ui-size-Icon-xl)',
      },

      minHeight: {
        // Aplicar valores legacy
        // ...spacingValues.minHeight,

        // Tamaños de controles
        'control-xxs': 'var(--ui-size-control-xxs)',
        'control-xs': 'var(--ui-size-control-xs)',
        'control-sm': 'var(--ui-size-control-sm)',
        'control-md': 'var(--ui-size-control-md)',
        'control-lg': 'var(--ui-size-control-lg)',

        // Tamaños de iconos
        'icon-xs': 'var(--ui-size-Icon-xs)',
        'icon-sm': 'var(--ui-size-Icon-sm)',
        'icon-md': 'var(--ui-size-Icon-md)',
        'icon-lg': 'var(--ui-size-Icon-lg)',
        'icon-xl': 'var(--ui-size-Icon-xl)',
      },

      maxHeight: {
        // Aplicar valores legacy
        // ...spacingValues.maxHeight,

        // Tamaños de controles
        'control-xxs': 'var(--ui-size-control-xxs)',
        'control-xs': 'var(--ui-size-control-xs)',
        'control-sm': 'var(--ui-size-control-sm)',
        'control-md': 'var(--ui-size-control-md)',
        'control-lg': 'var(--ui-size-control-lg)',

        // Tamaños de iconos
        'icon-xs': 'var(--ui-size-Icon-xs)',
        'icon-sm': 'var(--ui-size-Icon-sm)',
        'icon-md': 'var(--ui-size-Icon-md)',
        'icon-lg': 'var(--ui-size-Icon-lg)',
        'icon-xl': 'var(--ui-size-Icon-xl)',
      },

      minWidth: {
        // Aplicar valores legacy
        // ...spacingValues.minWidth,

        // Tamaños de controles
        'control-xxs': 'var(--ui-size-control-xxs)',
        'control-xs': 'var(--ui-size-control-xs)',
        'control-sm': 'var(--ui-size-control-sm)',
        'control-md': 'var(--ui-size-control-md)',
        'control-lg': 'var(--ui-size-control-lg)',

        // Tamaños de iconos
        'icon-xs': 'var(--ui-size-Icon-xs)',
        'icon-sm': 'var(--ui-size-Icon-sm)',
        'icon-md': 'var(--ui-size-Icon-md)',
        'icon-lg': 'var(--ui-size-Icon-lg)',
        'icon-xl': 'var(--ui-size-Icon-xl)',
      },

      maxWidth: {
        // Aplicar valores legacy
        // ...spacingValues.maxWidth,

        // Tamaños de controles
        'control-xxs': 'var(--ui-size-control-xxs)',
        'control-xs': 'var(--ui-size-control-xs)',
        'control-sm': 'var(--ui-size-control-sm)',
        'control-md': 'var(--ui-size-control-md)',
        'control-lg': 'var(--ui-size-control-lg)',

        // Tamaños de iconos
        'icon-xs': 'var(--ui-size-Icon-xs)',
        'icon-sm': 'var(--ui-size-Icon-sm)',
        'icon-md': 'var(--ui-size-Icon-md)',
        'icon-lg': 'var(--ui-size-Icon-lg)',
        'icon-xl': 'var(--ui-size-Icon-xl)',
      },

      // === TOKENS DE FIGMA - Gap ===
      gap: {
        ...spacingValues.gap,
        ...figmaSpacingScale,
      },

      // Aplicar el resto de valores de espaciado legacy
      // padding: spacingValues.padding,
      margin: {
        ...spacingValues.margin,
        ...figmaSpacingScale,
      },
      inset: {
        ...spacingValues.inset,
        ...figmaSpacingScale,
      },
      left: {
        ...spacingValues.left,
        ...figmaSpacingScale,
      },
      right: {
        ...spacingValues.right,
        ...figmaSpacingScale,
      },
      top: {
        ...spacingValues.top,
        ...figmaSpacingScale,
      },
      bottom: {
        ...spacingValues.bottom,
        ...figmaSpacingScale,
      },
      zIndex: {
        // Valores legacy
        dropdown: '1000',
        sticky: '1020',
        fixed: '1030',
        'modal-backdrop': '1040',
        modal: '1050',
        popover: '1060',
        tooltip: '1070',

        // === TOKENS DE FIGMA - Z-Index ===
        hide: 'var(--ui-z-hide)',
        base: 'var(--ui-z-base)',
        raised: 'var(--ui-z-raised)',
        shell: 'var(--ui-z-shell)',
        toast: 'var(--ui-z-toast)',
        overlay: 'var(--ui-z-overlay)',
        max: 'var(--ui-z-max)',
      },
      animation: {
        'progress-bar': 'progressBar 3s linear infinite',
        'bounce-in': 'bounce-in 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
        'bounce-out': 'bounce-out 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
        'fade-in': 'fade-in 0.3s ease-in-out',
        'fade-out': 'fade-out 0.3s ease-in-out',
        'dropdown-enter': 'dropdown-enter 0.2s ease-out',
        'dropdown-exit': 'dropdown-exit 0.2s ease-in',
      },
      transitionDuration: {
        dropdown: '200ms',
      },
      transitionTimingFunction: {
        dropdown: 'ease-out',
      },
      aspectRatio: {
        video: '16/9',
        square: '1/1',
        auto: 'auto',
      },
  };
}
